import { createWithEqualityFn as create } from 'zustand/traditional';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import type { MinimalPlacement } from '@ordr-as/ordr-service-types/epos';

interface PlacementsActions {
  setPlacements: (placements: MinimalPlacement[]) => void;
  setSelectedPlacementId: (id: number) => void;
  clear: () => void;
}

export interface PlacementsState {
  placements: MinimalPlacement[];
  selectedPlacementId: number | undefined;
  actions: PlacementsActions;
}

export const usePlacementsStore = create<PlacementsState>()(
  persist(
    (set, get) => ({
      actions: {
        clear: () => set({ placements: [], selectedPlacementId: undefined }),
        setPlacements: (placements) => {
          const currentSelectedId = get().selectedPlacementId;
          // Validate: clear selectedPlacementId if it's not in the new placements
          const isValid = currentSelectedId && placements.some((p) => p.id === currentSelectedId);
          set({
            placements,
            selectedPlacementId: isValid ? currentSelectedId : undefined,
          });
        },
        setSelectedPlacementId: (id) => {
          const placements = get().placements;
          const isValid = placements.some((p) => p.id === id);
          set({ selectedPlacementId: isValid ? id : undefined });
        },
      },
      placements: [],
      selectedPlacementId: undefined,
    }),
    {
      name: 'placements-storage',
      onRehydrateStorage: () => () => {
        // Migrate from legacy localStorage key after hydration
        // Note: Set directly without validation since placements aren't loaded yet.
        // Validation will occur when setPlacements is called.
        const legacyId = localStorage.getItem('placementId');
        if (legacyId) {
          const currentState = usePlacementsStore.getState();
          if (!currentState.selectedPlacementId) {
            const parsed = Number.parseInt(legacyId);
            if (!Number.isNaN(parsed)) {
              usePlacementsStore.setState({ selectedPlacementId: parsed });
            }
          }
          localStorage.removeItem('placementId');
        }
      },
      partialize: (state) => ({ selectedPlacementId: state.selectedPlacementId }),
    }
  )
);

export const usePlacements = () => usePlacementsStore((state) => state.placements);
export const usePlacementsActions = () => usePlacementsStore((state) => state.actions);

// Returns selected placement ID, falling back to first placement if none selected
export const useSelectedPlacementId = () => {
  const { placements, selectedPlacementId } = usePlacementsStore(
    (state) => ({
      placements: state.placements,
      selectedPlacementId: state.selectedPlacementId,
    }),
    shallow
  );

  // Return selected if valid, otherwise fall back to first placement
  if (selectedPlacementId && placements.some((p) => p.id === selectedPlacementId)) {
    return selectedPlacementId;
  }
  return placements[0]?.id;
};
